import './VerticalBarChart.scss';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Coordinate,
  LabelList,
  Tooltip,
  XAxis,
  XAxisTickContentProps,
  YAxis
} from 'recharts';
import { measureTextWidth } from '@src/utils/canvasTextUtils';
import { Neutral600, Neutral700 } from '@src/utils/colors';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH } from '../ChartConstants';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import {
  BarLabel,
  formatBarLabel,
  getCategoryTickLayout,
  getUniformBarLabelLines,
  TICK_ROTATION_DEGREES,
  truncateToWidth
} from './vertical-bar-chart-utils';

const BAR_MAX_WIDTH = 160;
const BAR_CORNER_RADIUS = 4;
const FONT_FAMILY = 'Geist, sans-serif';
const LABEL_FONT_SIZE = 11;
const LABEL_FONT = `600 ${LABEL_FONT_SIZE}px ${FONT_FAMILY}`;
const LABEL_LINE_HEIGHT = 13;
const LABEL_GAP = 6;
const TICK_FONT_SIZE = 11;
const TICK_FONT = `${TICK_FONT_SIZE}px ${FONT_FAMILY}`;
const Y_AXIS_WIDTH = 44;
// Headroom above the tallest bar for a two-line value label, so it is never clipped.
const PLOT_MARGIN = { top: LABEL_GAP + 2 * LABEL_LINE_HEIGHT + 2, right: 8, bottom: 4, left: 0 };
// Clear space kept between neighbouring labels / category names.
const SLOT_PADDING = 6;

// Rough max width to keep the tooltip on-screen near the right edge — ChartTooltip sizes to its
// content, so this is a conservative estimate rather than an exact measurement.
const TOOLTIP_ESTIMATED_WIDTH = 260;
const TOOLTIP_VIEWPORT_MARGIN = 12;

const measureLabel = (text: string): number => measureTextWidth(text, LABEL_FONT);
const measureTick = (text: string): number => measureTextWidth(text, TICK_FONT);

// A custom tick bypasses recharts' own <Text>, which would wrap or overlap names that don't fit
// their slot. Names sit flat when they all fit, otherwise they are rotated (see
// getCategoryTickLayout). Names wider than `maxWidth` are cut with an ellipsis; the full name
// stays in a <title>.
const CategoryTick = ({
  x = 0,
  y = 0,
  payload = { value: '', coordinate: 0, index: 0 },
  rotate = false,
  maxWidth = Infinity
}: Partial<XAxisTickContentProps> & { rotate?: boolean; maxWidth?: number }) => {
  const name = String(payload.value);
  const transform = rotate
    ? `rotate(-${TICK_ROTATION_DEGREES}, ${Number(x)}, ${Number(y)})`
    : undefined;
  return (
    <text
      x={Number(x)}
      y={Number(y)}
      dy='0.71em'
      textAnchor={rotate ? 'end' : 'middle'}
      transform={transform}
      fontSize={TICK_FONT_SIZE}
      fill={Neutral600}
    >
      <title>{name}</title>
      {truncateToWidth(name, maxWidth, measureTick)}
    </text>
  );
};

// Draws the label above one bar. recharts clones this element with the bar's position (x, y,
// width) and its index; `labelLines` holds the already-laid-out lines for every bar.
const BarLabelContent = ({
  x,
  y,
  width,
  index,
  labelLines
}: {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  index?: number;
  labelLines: string[][];
}) => {
  const lines = index === undefined ? undefined : labelLines[index];
  if (!lines) return null;
  const centerX = Number(x) + Number(width) / 2;
  const lastLineY = Number(y) - LABEL_GAP;
  return (
    <text textAnchor='middle' fontSize={LABEL_FONT_SIZE} fontWeight={600} fill={Neutral700}>
      {lines.map((line, lineIndex) => (
        <tspan
          key={line}
          x={centerX}
          y={lastLineY - (lines.length - 1 - lineIndex) * LABEL_LINE_HEIGHT}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

interface VerticalBarChartProps<T> {
  data: T[];
  // Field shown under each bar.
  categoryKey: keyof T & string;
  // Numeric field that sets each bar's height.
  valueKey: keyof T & string;
  // Names the value in the tooltip (e.g. "Results").
  valueLabel: string;
  getBarColor: (row: T, index: number) => string;
  // Text drawn above each bar and used as the tooltip value. The layout adapts to the space each
  // bar has: "1,637 (73%)" on one line, else stacked, else just the value. Defaults to no label
  // above the bars and the plain formatted value in the tooltip.
  getBarLabel?: (row: T) => BarLabel;
  height?: number;
  // Formats the Y axis ticks and, without `getBarLabel`, the tooltip value. Defaults to raw counts.
  valueFormatter?: (value: number) => string;
}

interface VerticalBarChartTooltipProps<T> {
  active?: boolean;
  payload?: { payload: T }[];
  coordinate?: Coordinate;
  plotEl: HTMLDivElement | null;
  categoryKey: keyof T & string;
  valueKey: keyof T & string;
  valueLabel: string;
  getBarColor: (row: T, index: number) => string;
  getBarLabel?: (row: T) => BarLabel;
  data: T[];
  valueFormatter: (value: number) => string;
}

// Same reasoning as HorizontalBarChart's tooltip: recharts' default tooltip lives inside the
// chart card's `overflow: hidden`, so it clips near the card edge. Portaling to `document.body`
// with a viewport-relative fixed position — converted from the plot's own bounding rect plus
// recharts' SVG-local `coordinate` — avoids the clip.
const VerticalBarChartTooltip = <T,>({
  active,
  payload,
  coordinate,
  plotEl,
  categoryKey,
  valueKey,
  valueLabel,
  getBarColor,
  getBarLabel,
  data,
  valueFormatter
}: VerticalBarChartTooltipProps<T>) => {
  const plotRect = plotEl?.getBoundingClientRect();
  if (!active || !payload || payload.length === 0 || !coordinate || !plotRect) return null;
  const row = payload[0].payload;

  const left = Math.min(
    plotRect.left + coordinate.x + TOOLTIP_VIEWPORT_MARGIN,
    window.innerWidth - TOOLTIP_ESTIMATED_WIDTH
  );
  const top = Math.max(
    plotRect.top + coordinate.y - TOOLTIP_VIEWPORT_MARGIN,
    TOOLTIP_VIEWPORT_MARGIN
  );

  return createPortal(
    <div style={{ position: 'fixed', left, top, zIndex: 1000, pointerEvents: 'none' }}>
      <ChartTooltip
        headerLabel={String(row[categoryKey])}
        items={[
          {
            label: valueLabel,
            value: getBarLabel
              ? formatBarLabel(getBarLabel(row))
              : valueFormatter(Number(row[valueKey])),
            color: getBarColor(row, data.indexOf(row))
          }
        ]}
      />
    </div>,
    document.body
  );
};

export const VerticalBarChart = <T,>({
  data,
  categoryKey,
  valueKey,
  valueLabel,
  getBarColor,
  getBarLabel,
  height = 300,
  valueFormatter = toPrettyNumber
}: VerticalBarChartProps<T>) => {
  const [plotEl, setPlotEl] = useState<HTMLDivElement | null>(null);

  return (
    <div className='vertical-bar-chart'>
      <div className='vertical-bar-chart-plot' ref={setPlotEl} style={{ height }}>
        <AutoSizer
          renderProp={({ width = 0, height: sizerHeight = 0 }) => {
            // Horizontal room each bar (and its label / category name) gets; every fit decision
            // below is made against this, so the chart re-lays-out as the screen resizes.
            const plotWidth = width - PLOT_MARGIN.left - PLOT_MARGIN.right - Y_AXIS_WIDTH;
            const pitch = plotWidth / Math.max(1, data.length);
            const slotWidth = Math.max(0, pitch - SLOT_PADDING);
            const tickLayout = getCategoryTickLayout(
              data.map(row => String(row[categoryKey])),
              pitch,
              TICK_FONT_SIZE,
              measureTick
            );
            const labelLines = getBarLabel
              ? getUniformBarLabelLines(data.map(getBarLabel), slotWidth, measureLabel)
              : [];

            return (
              <BarChart data={data} width={width} height={sizerHeight} margin={PLOT_MARGIN}>
                <CartesianGrid
                  stroke={CARTESIAN_GRID_STROKE}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  vertical={false}
                />
                <XAxis
                  dataKey={(row: T) => row[categoryKey]}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  height={tickLayout.axisHeight}
                  tick={<CategoryTick rotate={tickLayout.rotate} maxWidth={tickLayout.maxWidth} />}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={Y_AXIS_WIDTH}
                  tick={{ fontSize: TICK_FONT_SIZE, fill: Neutral600 }}
                  tickFormatter={(value: number) => valueFormatter(value)}
                />
                <Tooltip
                  isAnimationActive={false}
                  content={
                    <VerticalBarChartTooltip<T>
                      plotEl={plotEl}
                      categoryKey={categoryKey}
                      valueKey={valueKey}
                      valueLabel={valueLabel}
                      getBarColor={getBarColor}
                      getBarLabel={getBarLabel}
                      data={data}
                      valueFormatter={valueFormatter}
                    />
                  }
                />
                <Bar
                  dataKey={(row: T) => row[valueKey]}
                  name={valueLabel}
                  isAnimationActive={false}
                  maxBarSize={BAR_MAX_WIDTH}
                  radius={[BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0]}
                >
                  {data.map((row, index) => (
                    <Cell key={String(row[categoryKey])} fill={getBarColor(row, index)} />
                  ))}
                  {getBarLabel && (
                    <LabelList content={<BarLabelContent labelLines={labelLines} />} />
                  )}
                </Bar>
              </BarChart>
            );
          }}
        />
      </div>
    </div>
  );
};
